/**
 * my-page ヘッダーの所属チームバッジ用ヘルパー。
 *
 * workspace.users.team_name（例: `ac_nbt` / `m_bdt` / `log_lpt`）から、Div プレフィクスで
 * 色系統を決め（Mall=緑 / Logi=青）、aC Div はチームごとに固有色を割り当てる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business ユーザーの team_name を my-page ヘッダーの所属チームバッジ（ラベル + 背景色 + 文字色）に変換する
 * @graph-connects none
 */

/** 所属チームバッジの表示スタイル。 */
export interface TeamBadgeStyle {
  /** バッジに表示する短いチームラベル（英大文字）。 */
  label: string;
  /** 背景色（HEX）。 */
  bg: string;
  /** 文字色（HEX）。 */
  text: string;
}

/** Mall Div 主題色（緑）。CROSS_DIV_PMO_SCOPE.mall_div.badgeColor と一致。 @graph-connects none */
const MALL_COLOR = '#50BA52';

/** Logi Div 主題色（青）。CROSS_DIV_PMO_SCOPE.log_div.badgeColor と一致。 @graph-connects none */
const LOGI_COLOR = '#56C3DA';

/**
 * aC Div の各チーム固有色（team_name の `ac_` を除いた suffix をキーにする）。
 * @graph-connects none
 */
const AC_TEAM_COLORS: Readonly<Record<string, { bg: string; text: string }>> = {
  cdt: { bg: '#0277BD', text: '#FFFFFF' }, // 海蓝色
  crmt: { bg: '#EC4899', text: '#FFFFFF' }, // 粉色（FCT へ統合された旧チーム。過去 team_name 用に残置）
  bdt: { bg: '#EAB308', text: '#422006' }, // 黄色
  qdt: { bg: '#8BA63F', text: '#FFFFFF' }, // 抹茶色
  nbt: { bg: '#4B5563', text: '#FFFFFF' }, // 深灰色
  // 2026-08 の aC Div 改組で追加された 3 チーム。CROSS_DIV_PMO_UI.ac_div.teamColors と
  // 同じ色相（桃 / 青緑 / 灰）を濃色版で割り当て、色欠けによるバッジ非表示を防ぐ。
  // FCT は CRMT を統合したチームのため、旧 CRMT の粉色をそのまま引き継ぐ。
  fct: { bg: '#EC4899', text: '#FFFFFF' }, // 粉色（旧 CRMT から継承）
  clt: { bg: '#0D9488', text: '#FFFFFF' }, // 青緑色
  styt: { bg: '#64748B', text: '#FFFFFF' }, // 灰色（nbt の深灰とは別トーン）
};

/**
 * team_name から所属チームバッジスタイルを返す。
 * `m_` → Mall（緑）/ `log_` → Logi（青）/ `ac_` → aC Div の各チーム固有色。
 * いずれの Div プレフィクスにも該当しない（pi_div 等）場合や未設定の場合は null（バッジ非表示）。
 * @graph-connects none
 */
export function getTeamBadge(teamName: string | null | undefined): TeamBadgeStyle | null {
  if (!teamName) return null;
  const lower = teamName.toLowerCase();

  if (lower.startsWith('m_')) {
    const label = lower.slice(2).toUpperCase();
    return label ? { label, bg: MALL_COLOR, text: '#FFFFFF' } : null;
  }
  if (lower.startsWith('log_')) {
    const label = lower.slice(4).toUpperCase();
    return label ? { label, bg: LOGI_COLOR, text: '#FFFFFF' } : null;
  }
  if (lower.startsWith('ac_')) {
    const suffix = lower.slice(3);
    const color = AC_TEAM_COLORS[suffix];
    return color ? { label: suffix.toUpperCase(), bg: color.bg, text: color.text } : null;
  }
  return null;
}
